(() => {
  if (customElements.get('fluff-ugc-reviews')) return;

  customElements.define(
    'fluff-ugc-reviews',
    class extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        this.abortController = new AbortController();
        this.videos = Array.from(this.querySelectorAll('[data-fluff-ugc-card] video'));
        this.tracks = Array.from(this.querySelectorAll('[data-fluff-ugc-track]'));

        this.videos.forEach((video) => {
          video.controls = false;
        });

        this.querySelectorAll('[data-fluff-ugc-play]').forEach((button) => {
          button.addEventListener('click', this.handlePlayButton, {
            signal: this.abortController.signal,
          });
        });

        this.videos.forEach((video) => {
          video.addEventListener('play', this.handleVideoPlay, {
            signal: this.abortController.signal,
          });
          video.addEventListener('pause', this.handleVideoPause, {
            signal: this.abortController.signal,
          });
          video.addEventListener('ended', this.handleVideoEnded, {
            signal: this.abortController.signal,
          });
        });

        this.tracks.forEach((track) => {
          track.addEventListener('keydown', this.handleTrackKeydown, {
            signal: this.abortController.signal,
          });
        });

        document.addEventListener('shopify:block:select', this.handleBlockSelect, {
          signal: this.abortController.signal,
        });

        this.setupObservers();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.visibilityObserver?.disconnect();
        this.abortController = null;
      }

      setupObservers() {
        if (!('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
            });
          },
          { threshold: 0.2 }
        );

        this.videos.forEach((video) => this.visibilityObserver.observe(video));
      }

      handlePlayButton = (event) => {
        const card = event.currentTarget.closest('[data-fluff-ugc-card]');
        const video = card?.querySelector('video');
        if (!video) return;

        if (video.paused) {
          this.pauseOtherVideos(video);
          video.controls = true;
          video.play().catch(() => {
            video.controls = false;
            this.updateCardState(video, false);
          });
        } else {
          video.pause();
        }
      };

      handleVideoPlay = (event) => {
        const video = event.currentTarget;
        this.pauseOtherVideos(video);
        video.controls = true;
        this.updateCardState(video, true);
      };

      handleVideoPause = (event) => {
        const video = event.currentTarget;
        video.controls = false;
        this.updateCardState(video, false);
      };

      handleVideoEnded = (event) => {
        const video = event.currentTarget;
        video.controls = false;
        video.currentTime = 0;
        this.updateCardState(video, false);
      };

      pauseOtherVideos(activeVideo) {
        this.videos.forEach((video) => {
          if (video !== activeVideo && !video.paused) video.pause();
        });
      }

      updateCardState(video, isPlaying) {
        const card = video.closest('[data-fluff-ugc-card]');
        const button = card?.querySelector('[data-fluff-ugc-play]');
        card?.classList.toggle('is-playing', isPlaying);
        if (button) {
          button.setAttribute('aria-pressed', String(isPlaying));
          button.setAttribute(
            'aria-label',
            isPlaying ? 'إيقاف فيديو تجربة العميل مؤقتًا' : button.dataset.fluffUgcPlayLabel
          );
        }
      }

      handleTrackKeydown = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        const track = event.currentTarget;
        const item = track.querySelector('[data-fluff-ugc-card], .fluff-ugc-message-card');
        if (!item) return;

        event.preventDefault();
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
        const distance = item.getBoundingClientRect().width + gap;
        track.scrollBy({
          left: event.key === 'ArrowLeft' ? -distance : distance,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      handleBlockSelect = (event) => {
        if (!this.contains(event.target)) return;

        event.target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      };
    }
  );
})();
